import { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Modal, Button } from '../primitives';
import { buildVCard } from '../../lib/vcard';
import styles from './DocContactQR.module.css';

export interface DocContactQRProps {
  name?: string;
  role?: string;
  org?: string;
  phone?: string;
  email?: string;
  /** Volitelný popisek tlačítka. */
  label?: string;
  /** Pouze ikonka, bez textu. */
  compact?: boolean;
  /** Titulek dialogu. */
  title?: string;
  /**
   * Náhledový režim: místo textového tlačítka se vykreslí malý QR kód přímo
   * (jde i do tisku/PDF). Kliknutí otevře velký QR + stažení vCard.
   */
  preview?: boolean;
  /** Velikost náhledového QR v px (výchozí 46). */
  previewSize?: number;
}

/**
 * Tlačítko „📱 QR vizitka“, které otevře modal s vCard QR kódem
 * pro daný kontakt. Stejné chování jako v2GenSimpleContactQR
 * v původním frontendu (frontend/index.html:8880).
 */
export function DocContactQR({
  name,
  role,
  org,
  phone,
  email,
  label = 'QR vizitka',
  compact,
  title,
  preview,
  previewSize = 46,
}: DocContactQRProps) {
  const [open, setOpen] = useState(false);

  const trimmedName = (name ?? '').trim();
  const hasData = Boolean(trimmedName);
  const vcard = useMemo(
    () => buildVCard({ name: trimmedName, role, org, phone, email }),
    [trimmedName, role, org, phone, email],
  );

  async function handleDownloadVcard() {
    if (!vcard) return;
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${trimmedName.replace(/\s+/g, '_') || 'kontakt'}.vcf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  // Náhled bez dat nic nekreslí — prázdný řádek kontaktu nemá mít QR.
  if (preview && !hasData) return null;

  return (
    <>
      <button
        type="button"
        className={preview ? styles.triggerPreview : styles.trigger}
        onClick={() => setOpen(true)}
        disabled={!hasData}
        title={hasData ? 'Zobrazit QR vizitku' : 'Nejdřív vyplňte jméno kontaktní osoby'}
        aria-label={preview ? `QR vizitka: ${trimmedName}` : undefined}
      >
        {preview ? (
          <QRCodeSVG value={vcard} size={previewSize} level="M" bgColor="#ffffff" fgColor="#17181b" />
        ) : (
          <>
            <span aria-hidden>📱</span>
            {!compact && <span>{label}</span>}
          </>
        )}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={title ?? 'QR vizitka'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Zavřít
            </Button>
            <Button variant="secondary" onClick={handleDownloadVcard} disabled={!vcard}>
              ⬇ Stáhnout vCard
            </Button>
          </>
        }
      >
        {hasData ? (
          <div className={styles.card}>
            <div className={styles.qr}>
              <QRCodeSVG value={vcard} size={220} level="M" bgColor="#ffffff" fgColor="#17181b" />
            </div>
            <div className={styles.info}>
              <div className={styles.name}>{trimmedName}</div>
              {role && <div className={styles.role}>{role}</div>}
              {org && <div className={styles.org}>{org}</div>}
              {phone && <div className={styles.line}>📞 {phone}</div>}
              {email && <div className={styles.line}>✉ {email}</div>}
            </div>
            <div className={styles.hint}>Naskenujte telefonem — kontakt se uloží do adresáře.</div>
          </div>
        ) : (
          <div className={styles.empty}>Nejdřív vyplňte alespoň jméno kontaktní osoby.</div>
        )}
      </Modal>
    </>
  );
}
